import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLangFile } from '../../context/LanguageContext';

interface Employer {
  id: number;
  username: string;
  name: string;
}

interface Props {
  open: boolean;
  employers: Employer[];
  onClose: () => void;
  onSubmit: (employer: Employer) => void;
}

const useStyles = makeStyles((theme) => ({
  form: {
    padding: '20px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
  },
  formControl: {
    width: '100%',
  },
  note: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  connectButton: {
    marginTop: theme.spacing(1),
  },
  cancel: {
    fontSize: '14px',
    fontWeight: 400,
  },
}));

const ExatrekEmployerSelectionPopup = ({ open, employers, onClose, onSubmit }: Props) => {
  const classes = useStyles();
  const LangFile = useLangFile();
  const lang = LangFile.ExatrekEmployerSelection;

  const [selectedEmployer, setSelectedEmployer] = useState<Employer | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<{ value: unknown }>) => {
      const match = employers.find((emp) => emp.id === (e.target.value as number));
      const employer = match !== undefined ? match : null;
      setSelectedEmployer(employer);
    },
    [employers]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedEmployer) return;
      setLoading(true);
      onSubmit(selectedEmployer);
    },
    [selectedEmployer, onSubmit]
  );

  useEffect(() => {
    if (!open) {
      setSelectedEmployer(null);
      setLoading(false);
      return;
    }
    if (employers.length === 1) {
      setSelectedEmployer(employers[0]);
    }
  }, [open, employers]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <form className={classes.form} onSubmit={handleSubmit}>
          <Typography variant="h6" className={classes.title}>
            {lang.title}
          </Typography>
          <Typography variant="body2">{lang.description}</Typography>

          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>{lang.selectLabel}</InputLabel>
            <Select
              disabled={loading}
              value={selectedEmployer ? selectedEmployer.id : ''}
              onChange={handleChange}
              label={lang.selectLabel}
            >
              {employers.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {employers.length === 1 && (
            <Typography className={classes.note}>{lang.singleEmployerNote}</Typography>
          )}

          <Button
            type="submit"
            disabled={loading || !selectedEmployer}
            fullWidth
            variant="contained"
            color="primary"
            className={classes.connectButton}
          >
            {lang.connectButton}
          </Button>
        </form>

        <Box
          sx={{
            pt: 0.5,
            textAlign: 'center',
          }}
        >
          <Button onClick={onClose} disableRipple className={classes.cancel}>
            {LangFile.DataIntegrationDialog.cancel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ExatrekEmployerSelectionPopup);
